'use client';

import { useState, useCallback, useMemo } from 'react';
import {
  useProjectReferences,
  useDeleteProjectReference,
  useUpdateProjectReferenceCategory,
  useUpdateProjectReference,
  useMoveProjectReferenceSection,
} from './use-project-references';
import type {
  ProjectReference,
  ProjectReferenceCategory,
  ProjectReferenceAspect,
  ProjectReferenceSection,
} from '@/types/project-reference';
import {
  isImageReference,
  isDocumentReference,
  isSystemReference,
  effectiveAspects,
} from '@/types/project-reference';

interface UseProjectReferencesPanelOptions {
  enablePolling?: boolean;
  section?: ProjectReferenceSection;
}

// The four card "types" the unified type selector exposes. A View is an angle
// (separate entity, converted via materials/convert); Document / Reference /
// Output style are all the SAME project_reference row in different states:
//   document     = section 'brief'
//   reference    = section 'reference' + style/material/color aspects
//   output style = section 'reference' + category 'output' (aspects cleared)
export type MaterialCardType = 'view' | 'document' | 'reference' | 'output';

// Resolve the current card type of a reference from its stored state. (Views
// never reach this dialog — they are angles edited elsewhere.)
export function referenceCardType(
  reference: ProjectReference
): Exclude<MaterialCardType, 'view'> {
  if (reference.attributes.section === 'brief') return 'document';
  if (reference.attributes.category === 'output') return 'output';
  return 'reference';
}

export interface EnlargedImage {
  src: string;
  alt?: string;
}

export function useProjectReferencesPanel(
  projectId: string,
  options: UseProjectReferencesPanelOptions = {}
) {
  const { enablePolling = true, section } = options;

  const [enlargedImage, setEnlargedImage] = useState<EnlargedImage | null>(
    null
  );
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [selectedReference, setSelectedReference] =
    useState<ProjectReference | null>(null);

  // Fetch references with automatic polling for pending items
  const {
    data: references = [],
    isLoading,
    refetch,
  } = useProjectReferences(projectId, { enablePolling, section });

  // Mutations
  const deleteMutation = useDeleteProjectReference(projectId);
  const updateCategoryMutation = useUpdateProjectReferenceCategory(projectId);
  const updateReferenceMutation = useUpdateProjectReference(projectId);
  const moveSectionMutation = useMoveProjectReferenceSection(projectId);

  const handleDelete = useCallback(
    async (referenceId: string) => {
      try {
        setDeletingId(referenceId);
        await deleteMutation.mutateAsync(referenceId);
        // Close detail dialog if deleting the currently selected reference
        if (selectedReference?.id === referenceId) {
          setSelectedReference(null);
        }
      } catch (error) {
        console.error('Failed to delete reference:', error);
      } finally {
        setDeletingId(null);
      }
    },
    [deleteMutation, selectedReference?.id]
  );

  const handleCategoryChange = useCallback(
    async (referenceId: string, category: ProjectReferenceCategory) => {
      try {
        await updateCategoryMutation.mutateAsync({ referenceId, category });
      } catch (error) {
        console.error('Failed to update category:', error);
      }
    },
    [updateCategoryMutation]
  );

  const handleUploadSuccess = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleEnlarge = useCallback((src: string, alt?: string) => {
    setEnlargedImage({ src, alt });
  }, []);

  const handleCloseEnlarged = useCallback(() => {
    setEnlargedImage(null);
  }, []);

  // Detail dialog handlers
  const handleOpenDetail = useCallback((reference: ProjectReference) => {
    setSelectedReference(reference);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedReference(null);
  }, []);

  const handleSaveDetail = useCallback(
    async (data: {
      aspects?: ProjectReferenceAspect[];
      description?: string | null;
    }) => {
      if (!selectedReference) return;
      try {
        await updateReferenceMutation.mutateAsync({
          referenceId: selectedReference.id,
          ...data,
        });
        setSelectedReference(null);
      } catch (error) {
        console.error('Failed to update reference:', error);
      }
    },
    [selectedReference, updateReferenceMutation]
  );

  // Unified type change for the detail dialog's type selector. Document /
  // Reference / Output style are all the same project_reference row in different
  // states, reached by a single PATCH (the server clears aspects when switching
  // to output, and we seed style/material/color when switching to a reference).
  // View is a different entity, so it goes through materials/convert — and that
  // path only accepts a Document as the source, so a Reference/Output is first
  // re-filed to a Document (brief) before converting. The server validates every
  // hop and returns a verbatim 422 (e.g. "view has renders"); the mutations
  // surface it as-is — no client-side gating here. Closes the dialog on success.
  const handleChangeType = useCallback(
    async (target: MaterialCardType, convertDocToView?: () => Promise<void>) => {
      if (!selectedReference) return;
      const ref = selectedReference;
      const current = referenceCardType(ref);
      if (target === current) return;
      try {
        switch (target) {
          case 'document':
            await updateReferenceMutation.mutateAsync({
              referenceId: ref.id,
              section: 'brief',
            });
            break;
          case 'reference': {
            // Keep the item's own aspects if it already had any; otherwise seed
            // with the canonical default so a reference always has ≥1 aspect.
            const existing = effectiveAspects(ref);
            await updateReferenceMutation.mutateAsync({
              referenceId: ref.id,
              section: 'reference',
              aspects: existing.length > 0 ? existing : ['style'],
            });
            break;
          }
          case 'output':
            // Server clears aspects when category is set to output.
            await updateReferenceMutation.mutateAsync({
              referenceId: ref.id,
              section: 'reference',
              category: 'output',
            });
            break;
          case 'view':
            // materials/convert only accepts a Document as its source. From a
            // Reference/Output, first re-file to a brief Document, then convert.
            if (current !== 'document') {
              await updateReferenceMutation.mutateAsync({
                referenceId: ref.id,
                section: 'brief',
              });
            }
            await convertDocToView?.();
            break;
        }
        setSelectedReference(null);
      } catch (error) {
        console.error('Failed to change reference type:', error);
      }
    },
    [selectedReference, updateReferenceMutation]
  );

  // Re-home the open reference to the opposite section, then close the dialog.
  const handleMoveSection = useCallback(async () => {
    if (!selectedReference) return;
    const target: ProjectReferenceSection =
      selectedReference.attributes.section === 'brief' ? 'reference' : 'brief';
    try {
      await moveSectionMutation.mutateAsync({
        referenceId: selectedReference.id,
        section: target,
      });
      setSelectedReference(null);
    } catch (error) {
      console.error('Failed to move reference:', error);
    }
  }, [selectedReference, moveSectionMutation]);

  // Separate user and system references, then by type
  const categorizedReferences = useMemo(() => {
    const userReferences = references.filter((ref) => !isSystemReference(ref));
    const systemReferences = references.filter(isSystemReference);

    return {
      userReferences,
      systemReferences,
      userImageReferences: userReferences.filter(isImageReference),
      userDocumentReferences: userReferences.filter(isDocumentReference),
      systemImageReferences: systemReferences.filter(isImageReference),
    };
  }, [references]);

  return {
    // State
    isLoading,
    deletingId,
    enlargedImage,
    selectedReference,

    // References
    ...categorizedReferences,

    // Actions
    handleDelete,
    handleCategoryChange,
    handleUploadSuccess,
    handleEnlarge,
    handleCloseEnlarged,
    handleOpenDetail,
    handleCloseDetail,
    handleSaveDetail,
    handleMoveSection,
    handleChangeType,
    isSavingDetail: updateReferenceMutation.isPending,
    isDeletingDetail: deleteMutation.isPending,
    isMovingSection: moveSectionMutation.isPending,
  };
}
